import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import client from "@/api/client";
import {
  Loader2,
  RefreshCw,
  Globe,
  MapPin,
  Monitor,
  Smartphone,
  Tablet,
  ShieldCheck,
  KeyRound,
  Clock,
  ChevronLeft,
  ChevronRight,
  CalendarIcon,
  Search,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { ar as arLocale } from "date-fns/locale";

const PAGE_SIZE = 25;

function DeviceIcon({ device, className = "h-4 w-4" }) {
  if (device === "mobile") return <Smartphone className={className} />;
  if (device === "tablet") return <Tablet className={className} />;
  return <Monitor className={className} />;
}

function methodMeta(method, t) {
  if (method === "google_oauth") {
    return {
      label: t("access_log.methodGoogle", { defaultValue: "Google" }),
      className: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    };
  }
  return {
    label: t("access_log.methodQa", { defaultValue: "QA bypass" }),
    className: "bg-amber-500/10 text-amber-600 border-amber-500/20",
  };
}

function LocationCell({ entry }) {
  const parts = [entry.city, entry.region, entry.country].filter(Boolean);
  if (!parts.length) {
    return <span className="text-muted-foreground/60 text-xs">—</span>;
  }
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-xs">
      <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
      <span className="truncate min-w-0">{parts.join(", ")}</span>
    </span>
  );
}

export function AdminAccessLog() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [scope, setScope] = useState("all"); // all | admin | member
  const [q, setQ] = useState("");
  const [insights, setInsights] = useState(null);
  const isAr = i18n.language === "ar";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip, scope };
      if (q.trim()) params.q = q.trim();
      if (startDate) params.start_date = format(startDate, "yyyy-MM-dd");
      if (endDate) params.end_date = format(endDate, "yyyy-MM-dd");
      const res = await client.get("/admin/access-logs", { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error("Access log load failed:", e);
      toast.error(t("access_log.loadError", { defaultValue: "Failed to load access log" }));
    } finally {
      setLoading(false);
    }
  }, [skip, startDate, endDate, scope, q, t]);

  useEffect(() => {
    const id = setTimeout(load, 200);
    return () => clearTimeout(id);
  }, [load]);

  useEffect(() => {
    client.get("/admin/access-logs/summary")
      .then((res) => setInsights(res.data))
      .catch(() => {});
  }, []);

  const clearDateRange = () => {
    setStartDate(null);
    setEndDate(null);
    setQ("");
    setScope("all");
    setSkip(0);
  };

  const hasDateFilter = !!(startDate || endDate);

  const hasPrev = skip > 0;
  const hasNext = skip + PAGE_SIZE < total;
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const rangeStart = total === 0 ? 0 : skip + 1;
  const rangeEnd = Math.min(total, skip + PAGE_SIZE);

  // Simple anomaly heuristic: flag entries where country differs from the most-recent one
  const referenceCountry = useMemo(() => {
    return items.find((e) => e.country)?.country || "";
  }, [items]);

  const isAnomaly = (entry) => {
    if (!referenceCountry || !entry.country) return false;
    return entry.country !== referenceCountry;
  };

  const formatWhen = (iso) => {
    try {
      const d = parseISO(iso);
      const locale = isAr ? arLocale : undefined;
      return {
        rel: formatDistanceToNow(d, { addSuffix: true, locale }),
        abs: format(d, "MMM d, yyyy · HH:mm", { locale }),
      };
    } catch (e) {
      return { rel: "", abs: iso };
    }
  };

  return (
    <Card
      className="rounded-2xl border border-border p-4 sm:p-6 space-y-4 sm:space-y-6"
      data-testid="admin-access-log-panel"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0">
            <KeyRound className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-lg sm:text-xl font-semibold truncate">
              {t("access_log.title", { defaultValue: "Access log" })}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground">
              {t("access_log.subtitle", {
                defaultValue: "Recent admin sign-ins with IP, location and device.",
              })}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-xl gap-2 shrink-0 w-full sm:w-auto"
          onClick={() => {
            setSkip(0);
            load();
          }}
          disabled={loading}
          data-testid="admin-access-log-refresh"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          <span>{t("access_log.refresh", { defaultValue: "Refresh" })}</span>
        </Button>
      </div>

      {/* Date range filter */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 pb-2 border-b border-border">
        <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground shrink-0">
          {t("access_log.dateRange", { defaultValue: "Date range" })}
        </span>
        <div className="flex flex-wrap items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg h-9 gap-1.5 justify-start font-normal min-w-[140px]"
                data-testid="admin-access-log-start-date"
              >
                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                {startDate ? format(startDate, "MMM d, yyyy") : t("access_log.from", { defaultValue: "From" })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(d) => { setStartDate(d || null); setSkip(0); }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          <span className="text-xs text-muted-foreground">→</span>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="rounded-lg h-9 gap-1.5 justify-start font-normal min-w-[140px]"
                data-testid="admin-access-log-end-date"
              >
                <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                {endDate ? format(endDate, "MMM d, yyyy") : t("access_log.to", { defaultValue: "To" })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(d) => { setEndDate(d || null); setSkip(0); }}
                disabled={startDate ? { before: startDate } : undefined}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {hasDateFilter && (
            <Button
              variant="ghost"
              size="sm"
              className="rounded-lg h-9 gap-1 text-xs text-muted-foreground"
              onClick={clearDateRange}
              data-testid="admin-access-log-clear-dates"
            >
              <X className="h-3.5 w-3.5" />
              {t("access_log.clearDates", { defaultValue: "Clear" })}
            </Button>
          )}
        </div>
      </div>

      {/* Scope: admins vs registered members (legacy rows have no role and
          are all admin logins). */}
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="flex gap-1.5" data-testid="access-log-scope">
          {["all", "admin", "member"].map((sc) => (
            <button
              key={sc}
              type="button"
              onClick={() => { setScope(sc); setSkip(0); }}
              className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
                scope === sc
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background text-muted-foreground border-border hover:border-primary/40"
              }`}
              data-testid={`access-log-scope-${sc}`}
            >
              {t(`access_log.scope_${sc}`)}
            </button>
          ))}
        </div>
        <div className="relative sm:ms-auto sm:w-72">
          <Search className="h-4 w-4 text-muted-foreground absolute start-3 top-1/2 -translate-y-1/2" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); setSkip(0); }}
            placeholder={t("access_log.searchPlaceholder")}
            className="rounded-xl ps-9 h-10"
            data-testid="access-log-search"
          />
        </div>
      </div>

      {/* Registered-user sign-in insights */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3" data-testid="access-log-insights">
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("access_log.membersSignedIn")}</p>
          <p className="text-xl sm:text-2xl font-display font-semibold mt-1 tabular-nums" dir="ltr" data-testid="access-log-unique-members">
            {insights ? insights.unique_members_30d : "—"}
          </p>
          <p className="text-[10px] text-muted-foreground truncate">
            {insights ? `${insights.registered_users} ${t("access_log.registered")}` : ""}
          </p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("access_log.memberSignins30d")}</p>
          <p className="text-xl sm:text-2xl font-display font-semibold mt-1 tabular-nums" dir="ltr" data-testid="access-log-member-signins">
            {insights ? insights.member_signins_30d : "—"}
          </p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("access_log.adminSignins30d")}</p>
          <p className="text-xl sm:text-2xl font-display font-semibold mt-1 tabular-nums" dir="ltr" data-testid="access-log-admin-signins">
            {insights ? insights.admin_signins_30d : "—"}
          </p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{t("access_log.today")}</p>
          <p className="text-xl sm:text-2xl font-display font-semibold mt-1 tabular-nums" dir="ltr" data-testid="access-log-today">
            {insights ? insights.today : "—"}
          </p>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {t("access_log.totalSignIns", { defaultValue: "Total sign-ins" })}
          </p>
          <p className="text-xl sm:text-2xl font-display font-semibold mt-1 tabular-nums" data-testid="access-log-total">
            {loading && items.length === 0 ? <span className="text-muted-foreground/40">—</span> : total}
          </p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {t("access_log.lastEntry", { defaultValue: "Last entry" })}
          </p>
          <p className="text-xs sm:text-sm font-medium mt-1 truncate">
            {items.length ? formatWhen(items[0].created_at).rel : "—"}
          </p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {t("access_log.primaryLocation", { defaultValue: "Primary region" })}
          </p>
          <p className="text-xs sm:text-sm font-medium mt-1 truncate">{referenceCountry || "—"}</p>
        </div>
        <div className="rounded-xl border border-border p-3">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {t("access_log.uniqueIps", { defaultValue: "Unique IPs" })}
          </p>
          <p className="text-xl sm:text-2xl font-display font-semibold mt-1 tabular-nums">
            {new Set(items.map((e) => e.ip).filter(Boolean)).size}
          </p>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto rounded-xl border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[180px]">
                {t("access_log.colWhen", { defaultValue: "When" })}
              </TableHead>
              <TableHead>{t("access_log.colEmail", { defaultValue: "Email" })}</TableHead>
              <TableHead>{t("access_log.colIp", { defaultValue: "IP" })}</TableHead>
              <TableHead>{t("access_log.colLocation", { defaultValue: "Location" })}</TableHead>
              <TableHead>{t("access_log.colDevice", { defaultValue: "Device" })}</TableHead>
              <TableHead className="w-[110px]">{t("access_log.colMethod", { defaultValue: "Method" })}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin inline" />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="py-10 text-center text-muted-foreground">
                  {t("access_log.empty", { defaultValue: "No sign-ins yet." })}
                </TableCell>
              </TableRow>
            ) : (
              items.map((entry) => {
                const method = methodMeta(entry.method, t);
                const when = formatWhen(entry.created_at);
                const anomaly = isAnomaly(entry);
                return (
                  <TableRow
                    key={entry.log_id}
                    className={anomaly ? "bg-amber-500/5" : undefined}
                    data-testid={`access-log-row-${entry.log_id}`}
                  >
                    <TableCell className="align-top">
                      <div className="flex items-start gap-2">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                        <div className="min-w-0">
                          <p className="text-xs font-medium truncate">{when.rel}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{when.abs}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <p className="text-sm font-medium truncate">{entry.name || entry.email}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${
                            (entry.role || "admin") === "admin"
                              ? "border-primary/40 text-primary"
                              : "border-border text-muted-foreground"
                          }`}
                          data-testid={`access-log-role-${entry.log_id}`}
                        >
                          {t(`roles.${entry.role || "admin"}`, { defaultValue: entry.role || "admin" })}
                        </Badge>
                        <span className="text-[11px] text-muted-foreground truncate">{entry.email}</span>
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                        {entry.ip || "—"}
                      </code>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="flex items-center gap-2">
                        <LocationCell entry={entry} />
                        {anomaly && (
                          <Badge
                            variant="outline"
                            className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-500/30"
                            data-testid={`access-log-anomaly-${entry.log_id}`}
                          >
                            {t("access_log.anomaly", { defaultValue: "Unusual" })}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="flex items-center gap-2 text-xs">
                        <DeviceIcon device={entry.device} className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="truncate">
                          {entry.browser} · {entry.os}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <Badge variant="outline" className={`text-[10px] ${method.className}`}>
                        {method.label}
                      </Badge>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile card list */}
      <div className="md:hidden space-y-3">
        {loading && items.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline" />
          </div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground text-sm">
            {t("access_log.empty", { defaultValue: "No sign-ins yet." })}
          </div>
        ) : (
          items.map((entry) => {
            const method = methodMeta(entry.method, t);
            const when = formatWhen(entry.created_at);
            const anomaly = isAnomaly(entry);
            return (
              <div
                key={entry.log_id}
                className={`rounded-xl border p-3 space-y-2 ${
                  anomaly ? "border-amber-500/40 bg-amber-500/5" : "border-border"
                }`}
                data-testid={`access-log-card-${entry.log_id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{entry.name || entry.email}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{entry.email}</p>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${
                          (entry.role || "admin") === "admin"
                            ? "border-primary/40 text-primary"
                            : "border-border text-muted-foreground"
                        }`}
                      >
                        {t(`roles.${entry.role || "admin"}`, { defaultValue: entry.role || "admin" })}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground">{when.rel}</span>
                    </div>
                  </div>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${method.className}`}>
                    {method.label}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border/60">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      {t("access_log.colIp", { defaultValue: "IP" })}
                    </p>
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono break-all">
                      {entry.ip || "—"}
                    </code>
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      {t("access_log.colLocation", { defaultValue: "Location" })}
                    </p>
                    <LocationCell entry={entry} />
                  </div>
                  <div className="col-span-2 min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      {t("access_log.colDevice", { defaultValue: "Device" })}
                    </p>
                    <div className="flex items-center gap-2 text-xs">
                      <DeviceIcon
                        device={entry.device}
                        className="h-3.5 w-3.5 text-muted-foreground shrink-0"
                      />
                      <span className="truncate">
                        {entry.browser} · {entry.os}
                      </span>
                    </div>
                  </div>
                </div>

                {anomaly && (
                  <div className="flex items-center gap-1.5 text-[11px] text-amber-600 pt-1">
                    <Globe className="h-3 w-3" />
                    {t("access_log.anomalyHelp", {
                      defaultValue: "Country differs from your usual sign-in region.",
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {total > PAGE_SIZE && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-border">
          <p className="text-xs text-muted-foreground text-center sm:text-left">
            {t("access_log.showing", {
              defaultValue: "Showing {{start}}–{{end}} of {{total}}",
              start: rangeStart,
              end: rangeEnd,
              total,
            })}
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg h-9 gap-1"
              onClick={() => setSkip((s) => Math.max(0, s - PAGE_SIZE))}
              disabled={!hasPrev || loading}
              data-testid="access-log-prev"
            >
              {isAr ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
              <span className="text-xs">{t("common.previous", { defaultValue: "Prev" })}</span>
            </Button>
            <span className="text-xs text-muted-foreground tabular-nums px-1">
              {currentPage} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="rounded-lg h-9 gap-1"
              onClick={() => setSkip((s) => s + PAGE_SIZE)}
              disabled={!hasNext || loading}
              data-testid="access-log-next"
            >
              <span className="text-xs">{t("common.next", { defaultValue: "Next" })}</span>
              {isAr ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}

      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 pt-2 border-t border-border">
        <ShieldCheck className="h-3 w-3 text-primary" />
        {t("access_log.retention", {
          defaultValue: "Entries older than 90 days are automatically purged.",
        })}
      </p>
    </Card>
  );
}

export default AdminAccessLog;
